// FilterBar: shared filter/sort/search toolbar for the library browse
// pages (artists, albums, playlists, songs). State lives in the URL
// query string (?q=&sort=&order=&...), so a filtered view survives
// reload and back-navigation the same way ?page= does.
import { useEffect, useRef, useState } from 'react';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, ChevronDown, Search, X } from 'lucide-react';
import './FilterBar.css';

export type FilterSelect = {
    name: string;
    label: string;
    value: string;
    options: { value: string; label: string }[];
    // multi renders a checkbox dropdown that contributes repeated URL
    // params (?genre=a&genre=b); single-select keeps ?name=value.
    multi?: boolean;
};

export type FilterState = {
    q: string;
    sort: string;
    order: 'asc' | 'desc';
    extra: Record<string, string>;
    // multi holds selected values for the multi-selects, keyed by name.
    multi: Record<string, string[]>;
};

export type FilterSpec = {
    sorts: { value: string; label: string }[];
    defaultSort: string;
    defaultOrder: 'asc' | 'desc';
    selects: FilterSelect[];
};

// readFilters parses the current URL search params into FilterState,
// falling back to the spec defaults.
export function readFilters(spec: FilterSpec): FilterState {
    const params = new URLSearchParams(window.location.search);
    const order = params.get('order') === 'desc' ? 'desc' : params.get('order') === 'asc' ? 'asc' : spec.defaultOrder;
    const sort = params.get('sort') ?? spec.defaultSort;
    const extra: Record<string, string> = {};
    const multi: Record<string, string[]> = {};
    for (const sel of spec.selects) {
        if (sel.multi) {
            const values = params.getAll(sel.name).map((v) => v.trim()).filter(Boolean);
            multi[sel.name] = values;
            extra[sel.name] = '';
        } else {
            extra[sel.name] = params.get(sel.name) ?? '';
        }
    }
    return { q: params.get('q') ?? '', sort, order, extra, multi };
}

// writeFilters merges a FilterState into the URL query string, preserving
// ?page= (reset to 1: any filter/sort change invalidates the page window)
// and dropping empty params so URLs stay clean.
export function writeFilters(state: FilterState, selects: FilterSelect[]): void {
    const params = new URLSearchParams(window.location.search);
    const pageParams: Record<string, string> = {};
    params.forEach((v, k) => {
        if (k !== 'q' && k !== 'sort' && k !== 'order' && !selects.some((s) => s.name === k)) pageParams[k] = v;
    });
    pageParams.page = '1';

    const next = new URLSearchParams(pageParams);
    if (state.q.trim()) next.set('q', state.q.trim());
    if (state.sort !== '') next.set('sort', state.sort);
    if (state.order !== 'asc') next.set('order', state.order);
    for (const sel of selects) {
        if (sel.multi) {
            for (const v of state.multi[sel.name] ?? []) {
                if (v.trim()) next.append(sel.name, v.trim());
            }
        } else {
            const v = state.extra[sel.name] ?? '';
            if (v) next.set(sel.name, v);
        }
    }
    window.history.replaceState(null, '', `${window.location.pathname}?${next.toString()}`);
    // Notify listeners (the page's own usePagedUrl + our state) without a
    // navigation: pushState-based routers can miss replaceState changes.
    window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function FilterBar(props: {
    spec: FilterSpec;
    state: FilterState;
    onChange: (next: FilterState) => void;
    resultCount?: number;
}): React.JSX.Element {
    const { spec, state, onChange, resultCount } = props;
    const [draftQ, setDraftQ] = useState(state.q);

    // Keep the local draft in sync when the state is reset externally.
    useEffect(() => setDraftQ(state.q), [state.q]);

    const commit = (next: FilterState) => {
        writeFilters(next, spec.selects);
        onChange(next);
    };

    const submitQ = (e: React.FormEvent) => {
        e.preventDefault();
        commit({ ...state, q: draftQ });
    };

    const clearAll = () => {
        setDraftQ('');
        commit({ q: '', sort: spec.defaultSort, order: spec.defaultOrder, extra: {}, multi: {} });
    };

    const hasFilters =
        state.q.trim() !== '' ||
        state.sort !== spec.defaultSort ||
        state.order !== spec.defaultOrder ||
        Object.values(state.extra).some((v) => v !== '') ||
        Object.values(state.multi).some((vals) => vals.length > 0);

    return (
        <div className="filterBar">
            <form className="filterBarSearch" onSubmit={submitQ}>
                <Search size={15} className="filterBarSearchIcon" />
                <input
                    className="filterBarInput"
                    type="text"
                    placeholder="Search..."
                    value={draftQ}
                    onChange={(e) => setDraftQ(e.target.value)}
                />
                {draftQ && (
                    <button
                        type="button"
                        className="filterBarClearQ"
                        onClick={() => {
                            setDraftQ('');
                            commit({ ...state, q: '' });
                        }}
                        aria-label="Clear search"
                    >
                        <X size={14} />
                    </button>
                )}
            </form>

            {spec.selects.map((sel) =>
                sel.multi ? (
                    <MultiSelect
                        key={sel.name}
                        label={sel.label}
                        options={sel.options}
                        selected={state.multi[sel.name] ?? []}
                        onToggle={(value) => {
                            const current = state.multi[sel.name] ?? [];
                            const next = current.includes(value)
                                ? current.filter((v) => v !== value)
                                : [...current, value];
                            commit({ ...state, multi: { ...state.multi, [sel.name]: next } });
                        }}
                        onClear={() => commit({ ...state, multi: { ...state.multi, [sel.name]: [] } })}
                    />
                ) : (
                    <select
                        key={sel.name}
                        className="filterBarSelect"
                        value={state.extra[sel.name] ?? ''}
                        onChange={(e) => commit({ ...state, extra: { ...state.extra, [sel.name]: e.target.value } })}
                    >
                        <option value="">{sel.label}</option>
                        {sel.options.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                ),
            )}

            <select
                className="filterBarSelect"
                value={state.sort}
                onChange={(e) => commit({ ...state, sort: e.target.value })}
            >
                {spec.sorts.map((s) => (
                    <option key={s.value} value={s.value}>
                        Sort: {s.label}
                    </option>
                ))}
            </select>

            <button
                type="button"
                className="filterBarOrder"
                onClick={() => commit({ ...state, order: state.order === 'asc' ? 'desc' : 'asc' })}
                aria-label={state.order === 'asc' ? 'Ascending' : 'Descending'}
                title={state.order === 'asc' ? 'Ascending' : 'Descending'}
            >
                {state.order === 'asc' ? <ArrowUpNarrowWide size={15} /> : <ArrowDownWideNarrow size={15} />}
            </button>

            {hasFilters && (
                <button type="button" className="filterBarReset" onClick={clearAll}>
                    Reset
                </button>
            )}

            {typeof resultCount === 'number' && (
                <span className="filterBarCount">{resultCount} results</span>
            )}
        </div>
    );
}
// MultiSelect: a button-styled dropdown with checkboxes. The trigger shows
// "Label" when nothing is picked and "Label: first +N" once values are
// chosen, so the bar stays compact with several selections.
function MultiSelect(props: {
    label: string;
    options: { value: string; label: string }[];
    selected: string[];
    onToggle: (value: string) => void;
    onClear: () => void;
}): React.JSX.Element {
    const { label, options, selected, onToggle, onClear } = props;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    // Focus the search box when the menu opens so typing can start
    // immediately (the point of a searchable list).
    useEffect(() => {
        if (open) searchRef.current?.focus();
        else setQuery('');
    }, [open]);

    // Case-insensitive substring filter; selected entries always stay
    // visible so unchecking doesn't require clearing the query.
    const needle = query.trim().toLowerCase();
    const visible = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
    const visibleValues = new Set(visible.map((o) => o.value));
    const selectedShown = selected.filter((v) => !needle || visibleValues.has(v));

    const checked = new Set(selected);
    const summary =
        selected.length === 0 ? label : selected.length === 1 ? `${label}: 1` : `${label}: ${selected.length}`;

    return (
        <div className="filterBarMulti" ref={rootRef}>
            <button
                type="button"
                className={`filterBarMultiTrigger ${selected.length > 0 ? 'filterBarMultiActive' : ''}`}
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
            >
                <span className="filterBarMultiLabel">{summary}</span>
                <ChevronDown size={14} />
            </button>
            {open && (
                <div className="filterBarMultiMenu" role="listbox" aria-multiselectable="true">
                    <div className="filterBarMultiSearch">
                        <Search size={13} className="filterBarMultiSearchIcon" />
                        <input
                            ref={searchRef}
                            className="filterBarMultiSearchInput"
                            type="text"
                            placeholder="Filter..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    {selected.length > 0 && selectedShown.length > 0 && (
                        <button type="button" className="filterBarMultiClear" onClick={onClear}>
                            <X size={12} /> Clear
                        </button>
                    )}
                    {visible.length === 0 ? (
                        <div className="filterBarMultiEmpty">No matches</div>
                    ) : (
                        visible.map((o) => (
                            <label key={o.value} className="filterBarMultiOption">
                                <input
                                    type="checkbox"
                                    checked={checked.has(o.value)}
                                    onChange={() => onToggle(o.value)}
                                />
                                <span>{o.label}</span>
                            </label>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
